/* global localStorage */

import { Store } from 'redux'
import { Entity } from '../app/entity'
import Navigator from '../app/navigator'
import { ApplicationState } from '../redux/ApplicationState'
import { ProfilesState } from '../redux/profiles/ProfilesState'

/**
 * Loader classes like this consilidate all loading profileLists for a domain
 * concept.
 *
 * They define one (or multiple) load() methods which execute an AJAX call
 * through the Api. You may implement parameter remapping and cache checks in
 * here. For the loaded date there should be a dispatched action through Redux.
 *
 * The Loader also defines a (static) method which handles its own actions and
 * applies the corresponding changes to the global store state.
 */
const Loader = function(store: Store<ApplicationState>, api) {
    this.store = store
    this.api = api

    this.load = parameters => {
        const prunedParameters = (({ organization, application, profile, ...rest }) => rest)(parameters)
        if (isEmpty(prunedParameters)) {
            const state = this.recover()
            if (state && state.profiles.criteria) {
                const globalState = this.store.getState()
                if (globalState.tidewaysContext && globalState.tidewaysContext.route) {
                    const navigator = new Navigator(globalState.tidewaysContext.route)
                    navigator.replace({ ...state.profiles.criteria, parameters })
                    return
                }
            }
        }

        if (parameters.transactionIds) {
            parameters.opId = parameters.transactionIds
        }

        this.api.trigger('XhprofProfilerBundle.Profile.listData', parameters, null, this.persist)
    }

    this.loadForTracepoint = parameters => {
        this.api.triggerContinuously('XhprofProfilerBundle.Profile.listByTracepointData', parameters, null, this.persist)
    }

    this.loadOperation = parameters => {
        if (!parameters.opId) {
            parameters.opId = [parameters.operation]
        }

        this.api.triggerContinuously('XhprofProfilerBundle.Profile.listData', parameters)
    }

    this.persist = data => {
        const globalState = this.store.getState()
        if (!globalState.tidewaysContext) {
            return
        }

        const key = globalState.tidewaysContext.getLocalStorageKey()
        const serializedData = JSON.stringify({
            profiles: {
                criteria: data.criteria,
            },
        })
        localStorage.setItem(key, serializedData)
    }

    this.recover = () => {
        const globalState = this.store.getState()
        if (!globalState.tidewaysContext) {
            return
        }

        const key = globalState.tidewaysContext.getLocalStorageKey()
        const serializedState = localStorage.getItem(key)
        if (!serializedState) {
            return
        }

        const state = JSON.parse(serializedState)
        if (!state) {
            return
        }

        return state
    }
}

const isEmpty = obj => [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length

const initialGlobalState: ProfilesState = {
    criteria: null,
    profiles: null,
}

Loader.handleAction = (globalState: ProfilesState = initialGlobalState, action) => {
    switch (action.type) {
        case 'TIDEWAYS_ROUTE':
            if (action.routeChanged) {
                return {
                    criteria: new Entity(),
                    profiles: new Entity(),
                }
            }

            return {
                criteria: Entity.purge(globalState.criteria || new Entity()),
                profiles: Entity.purge(globalState.profiles || new Entity()),
            }
        case 'Profile.listData.success':
        case 'Profile.listByTracepointData.success':
            return {
                ...globalState,
                criteria: new Entity(action.data.criteria, 60),
                profiles: new Entity(action.data.profiles, 0),
            }
        case 'Profile.listData.error':
        case 'Profile.listByTracepointData.error':
            return {
                ...globalState,
                criteria: new Entity().setError(action.error),
                profiles: new Entity().setError(action.error),
            }
        case 'Profile.criteriaModified': {
            const criteria = globalState.criteria || new Entity()

            const data = { ...action.data }
            if (data.transactionIds) {
                data.opId = data.transactionIds
            }

            return {
                ...globalState,
                criteria: {
                    ...criteria,
                    data: {
                        ...criteria.data,
                        ...data,
                    },
                },
            }
        }
    }

    return globalState
}

export default Loader
